import { useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, ScrollText } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectAppData, selectAuth, selectUserInformation, setAppData } from '../../../store/reducers';
import { Icon } from '../../atoms';
import { DrawerItem } from '../DrawerItem/DrawerItem';
import { styles as inlineStyles } from './NavigationDrawer.style';
import styles from './NavigationDrawer.module.scss';

export function NavigationDrawer(props: any) {
  const auth = useAppSelector(selectAuth);
  const userInformation = useAppSelector(selectUserInformation);
  const appData = useAppSelector(selectAppData);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const currentRoute = location.pathname;

  const isPatientDeclined =
    userInformation?.isPatientDeclined !== undefined && userInformation?.isPatientDeclined === true;

  const hasCompletedAppointment = userInformation.appointment?.some((appointment) => appointment.carriedOut === 1);

  const navigate = useNavigate();

  const navigateTo = (route: string, navigationParams?: string) => {
    const reduxData = {
      ...appData,
      // TODO: warum brauchen wir diese Zeile??
      // navigationDrawerOpen: !appData.navigationDrawerOpen,
      navigationDrawerOpen: false,
    };

    dispatch(setAppData(reduxData));

    // props.setToggleBarUp(false);
    if (navigationParams) {
      navigate({
        pathname: route,
        search: navigationParams,
      });

      return;
    }
    navigate(route);
  };

  const onClickClose = () => {
    const reduxData = {
      navigationDrawerOpen: !appData.navigationDrawerOpen,
    };

    dispatch(setAppData(reduxData));
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (event.currentTarget instanceof HTMLElement) {
        event.currentTarget.click();
      }
    }

    if (event.key === 'Escape') {
      onClickClose();
    }
  };

  // Function to handle the click on "Skip to content" button to focus on the main content area
  const skipToContentClick = () => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      const mainContentDiv = mainContent.querySelectorAll(':scope > div');
      const mainContentSection = mainContentDiv[1] as HTMLElement | undefined;

      if (mainContentSection) {
        mainContentSection.setAttribute('tabindex', '-1'); // ensure focusable if not already

        mainContentSection.focus();
      }
    }
  };

  const drawerClasses = [styles.NavigationDrawer];

  if (appData.navigationDrawerOpen) {
    drawerClasses.push(styles.open);
  }

  const drawerClassesString = drawerClasses.join(' ');

  return (
    <div className={drawerClassesString} style={inlineStyles.container} {...props}>
      <div className={styles.header}>
        <button type='button' className='skip-to-content' onClick={skipToContentClick}>
          Zum Inhalt
        </button>
        <div
          style={inlineStyles.logo}
          onClick={() => navigateTo('/dashboard')}
          onKeyDown={onKeyDown}
          role='button'
          tabIndex={0}
          data-test='1'
        >
          <Icon icon='nowomed' size={200} />
        </div>
        <div onClick={onClickClose} className={styles.close} onKeyDown={onKeyDown} role='button' tabIndex={0}>
          <Icon icon='cross' size={20} />
        </div>
      </div>
      <div className={styles.spacer} />
      {auth.isAuthenticated && (
        <>
          <div
            className={styles.item}
            onClick={() => navigateTo('/dashboard')}
            onKeyDown={onKeyDown}
            role='button'
            tabIndex={0}
          >
            <DrawerItem
              label='Dashboard'
              icon={() => <Icon icon='dashboard' color={currentRoute === '/dashboard' ? '#3db384' : 'grey'} />}
              labelStyle={currentRoute === '/dashboard' ? inlineStyles.labelGreen : inlineStyles.label}
            />
          </div>
          <div
            className={styles.item}
            onClick={() => navigateTo('/profile/documents', '?page=my-documents')}
            onKeyDown={onKeyDown}
            role='button'
            tabIndex={0}
          >
            <DrawerItem
              label='Meine Dokumente'
              icon={() => (
                <Icon icon='document' size={35} color={currentRoute === '/profile/documents' ? '#3db384' : 'grey'} />
              )}
              labelStyle={[
                currentRoute === '/profile/documents' ? inlineStyles.labelGreen : inlineStyles.label,
                { marginLeft: '1px' },
              ]}
            />
          </div>
        </>
      )}
      {!isPatientDeclined && auth.isAuthenticated && userInformation?.progress?.isDataConfirm && (
        <div
          className={styles.item}
          onClick={() => navigateTo('/patienten-tracking')}
          onKeyDown={onKeyDown}
          role='button'
          tabIndex={0}
        >
          <DrawerItem
            label='Therapie Monitoring'
            icon={() => (
              <Icon
                icon='healthTracking'
                color={currentRoute === '/patienten-tracking' ? '#3db384' : 'grey'}
                size={25}
              />
            )}
            labelStyle={currentRoute === '/patienten-tracking' ? inlineStyles.labelGreen : inlineStyles.label}
          />
        </div>
      )}
      {!isPatientDeclined && auth.isAuthenticated && userInformation?.appointment?.length > 0 && (
        <div
          className={styles.item}
          onClick={() => navigateTo('/my-pharmacy')}
          onKeyDown={onKeyDown}
          role='button'
          tabIndex={0}
        >
          <DrawerItem
            label='Meine Apotheke'
            icon={() => (
              <Icon icon='pharmacysmall' color={currentRoute === '/my-pharmacy' ? '#3db384' : 'grey'} size={25} />
            )}
            labelStyle={currentRoute === '/my-pharmacy' ? inlineStyles.labelGreen : inlineStyles.label}
          />
        </div>
      )}
      {auth.isAuthenticated && (
        <div
          className={styles.item}
          onClick={() => navigateTo('/my-account')}
          onKeyDown={onKeyDown}
          role='button'
          tabIndex={0}
        >
          <DrawerItem
            label='Mein Profil'
            icon={() => <Icon icon='user' color={currentRoute === '/my-account' ? '#3db384' : 'grey'} size={25} />}
            labelStyle={currentRoute === '/my-account' ? inlineStyles.labelGreen : inlineStyles.label}
          />
        </div>
      )}
      {auth.isAuthenticated && (
        <div
          className={styles.item}
          onClick={() => navigateTo('/payments')}
          onKeyDown={onKeyDown}
          role='button'
          tabIndex={0}
        >
          <DrawerItem
            label='Online-Zahlungen'
            icon={() => <CreditCard color={currentRoute === '/payments' ? '#3db384' : 'grey'} size={25} />}
            labelStyle={currentRoute === '/payments' ? inlineStyles.labelGreen : inlineStyles.label}
          />
        </div>
      )}
      {auth.isAuthenticated && hasCompletedAppointment && !isPatientDeclined && (
        <div
          className={styles.item}
          onClick={() => navigateTo('/certificates')}
          onKeyDown={onKeyDown}
          role='button'
          tabIndex={0}
        >
          <DrawerItem
            label='Bescheinigungen'
            icon={() => <ScrollText color={currentRoute === '/certificates' ? '#3db384' : 'grey'} size={25} />}
            labelStyle={currentRoute === '/certificates' ? inlineStyles.labelGreen : inlineStyles.label}
          />
        </div>
      )}
      <div className={styles.divider}></div>
      <div
        className={styles.item}
        onClick={() => navigateTo('/contact')}
        onKeyDown={onKeyDown}
        role='button'
        tabIndex={0}
      >
        <DrawerItem
          label='Hilfe / Kontakt'
          icon={() => <Icon icon='kontakt' color={currentRoute === '/contact' ? '#3db384' : 'grey'} />}
          labelStyle={currentRoute === '/contact' ? inlineStyles.labelGreen : inlineStyles.label}
        />
      </div>
      <div className={styles.divider}></div>
      <div className={styles.divider}></div>
      <div
        className={styles.item}
        onClick={() => {
          window.open('https://www.nowomed.com/de/impressum/', '_blank');
        }}
        onKeyDown={onKeyDown}
        role='button'
        tabIndex={0}
      >
        <DrawerItem label='Impressum' labelStyle={inlineStyles.labelWithoutIcon} />
      </div>
      <div
        className={styles.item}
        onClick={() => {
          navigateTo('/privacy-policy');
        }}
        onKeyDown={onKeyDown}
        role='button'
        tabIndex={0}
      >
        <DrawerItem
          label='Datenschutz'
          labelStyle={currentRoute === '/privacy-policy' ? inlineStyles.labelGreen : inlineStyles.label}
        />
      </div>
      <div
        className={styles.item}
        onClick={() => {
          navigateTo('/terms-and-conditions');
        }}
        onKeyDown={onKeyDown}
        role='button'
        tabIndex={0}
      >
        <DrawerItem
          label='AGB'
          labelStyle={currentRoute === '/terms-and-conditions' ? inlineStyles.labelGreen : inlineStyles.label}
        />
      </div>
    </div>
  );
}
